import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'next-i18next';
import Loading from '@components/Loading/Loading';
import styles from '@components/Video/VideoPlayer.styles';
import Icon from '@components/icons/Icon';
import theme from '@styles/theme';
import videoHasAudioTrack from '@utils/videoHasAudioTrack';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';
type PropsType = {
  video: string;
  loop: boolean;
  poster: string;
  play?: boolean;
  onLoaded?: () => void;
  onEnded?: () => void;
  onError?: () => void;
  withSound?: boolean;
  controls?: boolean;
  fitVideo?: boolean;
  fullscreen?: boolean;
  alt?: string;
  onClick?: () => void;
  soundToggler?: boolean;
};
const isPlayInterruptedError = (error: unknown) => error instanceof DOMException && error.name === 'AbortError';
const IOSVideoPlayer = ({
  video,
  loop,
  poster,
  play = false,
  onLoaded,
  onEnded,
  onError,
  withSound = false,
  controls = false,
  fitVideo = false,
  fullscreen = false,
  alt,
  onClick,
  soundToggler
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const [isPlaying, setIsPlaying] = useState(play);
  const [isLoading, setIsLoading] = useState(true);
  const [isSoundOn, setIsSoundOn] = useState(withSound);
  const [hasAudioTrack, setHasAudioTrack] = useState(false);
  const isSoundOnRef = useRef(isSoundOn);
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    isSoundOnRef.current = isSoundOn;
  }, [isSoundOn]);
  useEffect(() => {
    setIsSoundOn(withSound);
  }, [withSound]);
  useEffect(() => {
    setHasAudioTrack(false);
  }, [video]);
  const checkAudioTrack = useCallback(() => {
    if (videoRef.current && videoHasAudioTrack(videoRef.current)) {
      setHasAudioTrack(true);
    }
  }, []);
  const enableSound = useCallback(() => {
    const videoElement = videoRef.current;
    if (!videoElement?.muted) {
      return false;
    }
    videoElement.muted = false;
    setIsSoundOn(true);
    return true;
  }, []);
  const playVideo = useCallback(async () => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      return;
    }
    const wantsSound = isSoundOnRef.current;
    videoElement.muted = !wantsSound;
    try {
      await videoElement.play();
      setIsPlaying(true);
    } catch (error) {
      if (isPlayInterruptedError(error)) {
        return;
      }
      if (wantsSound) {
        videoElement.muted = true;
        try {
          await videoElement.play();
          setIsPlaying(true);
          setIsSoundOn(false);
        } catch (retryError) {
          if (isPlayInterruptedError(retryError)) {
            return;
          }
          if (process.env.NODE_ENV === 'development') {
            console.error('Error autoplaying video:', retryError);
          }
        }
        return;
      }
      if (process.env.NODE_ENV === 'development') {
        console.error('Error autoplaying video:', error);
      }
    }
  }, []);
  const pauseVideo = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, []);
  const handlePlay = () => {
    if (withSound && enableSound()) {
      void videoRef.current?.play();
      setIsPlaying(true);
      return;
    }
    if (videoRef.current) {
      void videoRef.current.play();
      setIsPlaying(true);
    }
  };
  const handleSoundToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsSoundOn(prev => {
      const next = !prev;
      if (videoRef.current) {
        videoRef.current.muted = !next;
      }
      return next;
    });
  };
  const handleVideoClick = () => {
    if (withSound && enableSound()) {
      return;
    }
    onClick?.();
  };
  const handleLoadedMetadata = () => {
    setIsLoading(false);
    checkAudioTrack();
    if (onLoaded) onLoaded();
    if (play) {
      void playVideo();
    }
  };
  const handleEnded = () => {
    if (loop && videoRef.current) {
      videoRef.current.currentTime = 0;
      void videoRef.current.play();
      return;
    }
    setIsPlaying(false);
    if (onEnded) onEnded();
  };
  const handleError = () => {
    setIsLoading(false);
    if (onError) onError();
  };
  useEffect(() => {
    const handleUserInteraction = () => {
      if (play) {
        void playVideo();
      }
    };
    document.addEventListener('touchstart', handleUserInteraction, {
      once: true
    });
    return () => {
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, [play, playVideo]);
  useEffect(() => {
    if (play) {
      void playVideo();
      const timeouts = [setTimeout(() => void playVideo(), 100), setTimeout(() => void playVideo(), 500)];
      return () => timeouts.forEach(clearTimeout);
    }
    pauseVideo();
    return undefined;
  }, [play, playVideo, pauseVideo]);
  return <div style={{
    position: 'relative',
    width: '100%',
    height: '100%'
  }}>
      {isLoading && <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 1
    }}>
          {poster ? <ResponsiveImage image={poster} alt="" sx={styles.thumbnail} /> : <Loading />}
        </div>}

      {soundToggler && hasAudioTrack && <button type="button" onClick={handleSoundToggle} sx={{
      ...styles.soundToggler,
      zIndex: 3
    }}>
          {!isSoundOn ? <Icon icon="SpeakerOff" color={theme.rawColors.neutralWhite} /> : <Icon icon="SpeakerOn" color={theme.rawColors.neutralWhite} />}
        </button>}

      {!isPlaying && !isLoading && <button type="button" onClick={handlePlay} style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'rgba(0, 0, 0, 0.5)',
      border: 'none',
      borderRadius: '50%',
      width: '80px',
      height: '80px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      zIndex: 2
    }}>
          <div style={{
        width: 0,
        height: 0,
        borderLeft: '20px solid white',
        borderTop: '12px solid transparent',
        borderBottom: '12px solid transparent',
        marginLeft: '4px'
      }} />
        </button>}

      <video ref={videoRef} src={video} playsInline webkit-playsinline="true" x5-playsinline="true" x-webkit-airplay="allow" loop={loop} muted={!isSoundOn} controls={controls} disablePictureInPicture={!fullscreen} preload="auto" style={{
      width: '100%',
      height: '100%',
      objectFit: fitVideo ? 'contain' : 'cover',
      display: isLoading ? 'none' : 'block'
    }} sx={styles.videoStyles(fullscreen, fitVideo)} onLoadedMetadata={handleLoadedMetadata} onPlaying={checkAudioTrack} onEnded={handleEnded} onPlay={() => setIsPlaying(true)} onPause={e => {
      const v = e.currentTarget;
      if (loop && v.ended) return;
      setIsPlaying(false);
    }} onError={handleError} onClick={handleVideoClick} aria-label={alt || t('productVideo')} />
    </div>;
};
export default IOSVideoPlayer;